/**
 * Клиент для интеграции с сервисами Базы данных цен (БДЦ)
 * https://e-catalog.gov.kz
 */

import store from "@/services/store";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface AttrItem {
    /** Технический ключ атрибута, например "ves" */
    key: string;
    value: string;
}

export interface AttributeItem {
    /** Человекочитаемый ключ атрибута, например "Вес" */
    key: string;
    value: string;
}

export interface AnalysisRequest {
    category_id: number;
    attrs: AttrItem[];
    /** Код КАТО региона госоргана, ведущего бюджетное планирование */
    kato: string;
}

export interface AnalysisContent {
    attrs: AttrItem[];
    is_domestic: boolean;
    inflation_price: number;
    median_price: number;
    price_min: number;
    price_max: number;
    count: number;
    source: string;
    sources: Record<string, number>[];
    kztin: string;
    source_details: string;
}

export interface AnalysisResponse {
    status: number;
    content: AnalysisContent;
    /** Токен расчёта - нужно передать без изменений в /analysis/save для этого же расчёта */
    calculation_token: string;
}

export interface SaveAnalysisRequest {
    // Обязательные поля
    date: string;           // "DD.MM.YYYY"
    time: string;           // "HH:MM:SS"
    kato: string;
    oktru_code: string;
    category_name: string;
    category_id: number;
    bzFormId: string;
    factId: string;
    iin: string;
    url: string;
    source_system_id: number;
    certificateType: number;

    // Атрибуты в двух форматах
    attributes: AttributeItem[];
    attrs: AttrItem[];

    // Результат анализа
    status: number;
    is_domestic: boolean;
    inflation_price: number | null;
    median_price: number;
    price_min: number;
    price_max: number;
    count: number;
    source: string;
    sources: Record<string, number>[];
    kztin: string;
    source_details: string;
    calculation_token: string;
}

export interface SaveAnalysisResponse {
    pdf: string;
    koh: string;
    sign_data: number;
    sma_doc: number;
    sma_result: number;
    isgp: number;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

// Запросы идут через наш Python-сервис sit-py: он сам получает токен БДЦ.
// Пользователь авторизуется своим Bearer-токеном (как и для остальных вызовов /api-py).
const ANALYSIS_URL = "/api-py/bdc/analysis";
const SAVE_ANALYSIS_URL = "/api-py/bdc/analysis/save";

async function request<T>(url: string, body: unknown): Promise<T> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };
    const userToken = store.getters.token;
    if (userToken) {
        headers["Authorization"] = `Bearer ${userToken}`;
    }

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`БДЦ вернул ${response.status}: ${text}`);
    }

    return response.json() as Promise<T>;
}

/**
 * Расчёт медианной цены (analysis).
 */
export async function analysis(payload: AnalysisRequest): Promise<AnalysisResponse> {
    return request<AnalysisResponse>(ANALYSIS_URL, payload);
}

/**
 * Фиксация результата и формирование PDF-акта (save_analysis).
 */
export async function saveAnalysis(payload: SaveAnalysisRequest): Promise<SaveAnalysisResponse> {
    return request<SaveAnalysisResponse>(SAVE_ANALYSIS_URL, payload);
}

export interface SaveOptions {
    kato: string;
    iin: string;
    url: string;
    source_system_id: number;
    certificateType?: number;
}

interface EnstruEntry {
    id: number;
    enstru: {
        id: number;
        name: string;
        code: string;
        short_description: string;
    };
    timestamp: number;
}

interface ProductAttribute {
    name: {
        kz: string;
        ru: string;
    };
    value: {
        kz: string;
        ru: string;
    };
    is_main: boolean;
    value_code: string;
    is_tech_spec: boolean;
    is_required_egz: boolean;
    is_required_kgd: boolean;
}

interface Category {
    id: number;
    name: string;
    code: string | null;
    full_code: string;
    level: number;
    parent: number;
    category_type: number;
    image: string | null;
}

interface ProductImage {
    id: number;
    order: number;
    image_url: string;
    hidden: boolean;
}

interface Product {
    id: number;
    external_id: string | null;
    name: string;
    is_domestic: boolean;
    is_social: boolean;
    product_type: number;
    model_name: string | null;
    gtin: string | null;
    mpn: string | null;
    kztin_code: string;
    net_weight: number | null;
    net_measure: string | null;
    gross_weight: number | null;
    gross_measure: string | null;
    volume: number | null;
    standards: string | null;
    volume_measure: string | null;
    manufacturer: string | null;
    amount_in_package: number | null;
    package_dimensions: string | null;
    product_dimensions: string | null;
    hierarchy: number;
    source: number;
    status: number;
    created: number;
    modified: number;
    oked: string | null;
    category: Category;
    tnved: string | null;
    tnveds: any[];
    brand: string | null;
    measure: string | null;
    country: string | null;
    child_product: any | null;
    attributes_dict: Record<string, ProductAttribute>;
    enstrus: EnstruEntry[];
    images: ProductImage[];
    documents: any[];
    active_product_kztin: string | null;
    deactivation_reason: string | null;
    active_product_id: number | null;
    manufacturer_bin: string | null;
    ntin_code: string;
    ntin_isdeactivated: boolean;
    ntin_deactivationreason: string | null;
    ntin_duplicateofproduct: string | null;
    ntin_isdeactivated_at: string | null;
    ntin_active_product_ids: number[];
}

export async function analyzeAndSave(product: Product, data: Omit<AnalysisRequest, "kato">, opts: SaveOptions) {
    // 1. Расчёт цены
    // const analysisResult = await analysis(
    //     {
    //         category_id: product.category.id,
    //         attrs: Object.entries(product.attributes_dict).map(([key, attr]) => ({
    //             key,
    //             value: attr.value.ru,
    //         })),
    //     }
    // );

    const analysisResult = await analysis({ ...data, kato: opts.kato });

    const content = analysisResult.content ?? {} as AnalysisContent;

    // 2. Фиксация результата
    const now = new Date();
    const date = now.toLocaleDateString("ru-KZ", { day: "2-digit", month: "2-digit", year: "numeric" });
    const time = now.toLocaleTimeString("ru-KZ", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

    const saveResult = await saveAnalysis(
        {
            date,
            time,
            kato: opts.kato,
            oktru_code: product.category.full_code,
            category_name: product.category.name,
            category_id: product.category.id,
            bzFormId: String(product.id),
            factId: `${product.category.id}_${product.id}`,
            iin: opts.iin,
            url: opts.url,
            source_system_id: 3,
            certificateType: 1,

            attributes: Object.values(product.attributes_dict || {}).map((attr) => ({
                key: attr.name.ru,
                value: attr.value.ru,
            })),

            attrs: content.attrs ?? [],

            status: analysisResult.status,
            is_domestic: content.is_domestic ?? false,
            inflation_price: content.inflation_price ?? null,
            median_price: content.median_price ?? 0,
            price_min: content.price_min ?? 0,
            price_max: content.price_max ?? 0,
            count: content.count ?? 0,
            source: content.source ?? "",
            sources: content.sources ?? [],
            kztin: content.kztin ?? "",
            source_details: content.source_details ?? "",
            calculation_token: analysisResult.calculation_token,
        }
    );

    return {
        analysisResult,
        saveResult,
    };
}

